/**
 * Folder Warning Dialog Component
 *
 * Shows warning when updating folder denomination affects existing files.
 * Allows user to confirm whether to apply changes to folder contents.
 */

import React, { useState } from 'react';
import { useFolderStore } from '../stores/folderStore';

export const FolderWarningDialog: React.FC = () => {
  const {
    isWarningDialogOpen,
    selectedFolder,
    selectedFolderFilesCount,
    isSaving,
    closeWarningDialog,
    updateSelectedFolder,
  } = useFolderStore();

  const [applyToContents, setApplyToContents] = useState(false);

  const handleClose = () => {
    setApplyToContents(false);
    closeWarningDialog();
  };

  const handleConfirm = async () => {
    if (!selectedFolder) return;

    // The actual update params were saved in the edit dialog
    // For now, we'll just update with the new denomination from the folder
    // In a real scenario, you'd want to pass the full update params
    await updateSelectedFolder(
      {
        denomination_id: selectedFolder.denomination_id,
        document_type: selectedFolder.document_type,
        is_official: selectedFolder.is_official,
        description: selectedFolder.description || undefined,
      },
      applyToContents
    );
  };

  if (!isWarningDialogOpen || !selectedFolder) return null;

  const fileCount = selectedFolderFilesCount || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-card rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-yellow-500/30 bg-yellow-500/10">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-white">
              Warning: Folder Contains Files
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-dark-text-muted hover:text-white transition-colors"
            disabled={isSaving}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-white">
            This folder <strong>"{selectedFolder.folder_path}"</strong> contains{' '}
            <strong className="text-accent-blue">{fileCount} file{fileCount !== 1 ? 's' : ''}</strong>.
          </p>

          <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
            <p className="text-sm text-white font-medium mb-3">
              What would you like to do?
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-bridge-hover transition-colors">
                <input
                  type="radio"
                  name="apply-choice"
                  checked={!applyToContents}
                  onChange={() => setApplyToContents(false)}
                  className="mt-1 text-accent-blue bg-dark-bg border-dark-border focus:ring-2 focus:ring-accent-blue focus:ring-offset-0"
                  disabled={isSaving}
                />
                <div className="flex-1">
                  <div className="font-medium text-white">Update folder only</div>
                  <div className="text-sm text-dark-text-muted mt-1">
                    Keep existing files unchanged. New files will inherit the new denomination.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-bridge-hover transition-colors">
                <input
                  type="radio"
                  name="apply-choice"
                  checked={applyToContents}
                  onChange={() => setApplyToContents(true)}
                  className="mt-1 text-accent-blue bg-dark-bg border-dark-border focus:ring-2 focus:ring-accent-blue focus:ring-offset-0"
                  disabled={isSaving}
                />
                <div className="flex-1">
                  <div className="font-medium text-white">
                    Update folder and all {fileCount} file{fileCount !== 1 ? 's' : ''}
                  </div>
                  <div className="text-sm text-dark-text-muted mt-1">
                    All files in this folder will inherit the new denomination settings immediately.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {applyToContents && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-sm text-yellow-200">
                <strong>Important:</strong> This will update the denomination metadata for all {fileCount}{' '}
                file{fileCount !== 1 ? 's' : ''} in this folder. This action cannot be undone.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end p-4 border-t border-dark-border">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-white bg-dark-bg hover:bg-bridge-hover border border-dark-border rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-white bg-gradient-to-r from-accent-blue to-accent-orange rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
            disabled={isSaving}
          >
            {isSaving ? 'Updating...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderWarningDialog;
